import { FormEvent, useCallback, useMemo } from "react";
import { TextInput, Textarea, Stack, Switch, Select } from "@mantine/core";
import { useForm } from "@mantine/form";
import { Stain, StainType } from "../../../services/admin/stains.service";
import { useCptCodesAdmin } from "../../../hooks/admin/useCptCodes";
import { useActiveStainProviders } from "../../../hooks/admin/useStains";
import { required, scrollToFirstError } from "../../../utils/formValidation";

const STAIN_TYPE_OPTIONS: { value: StainType; label: string }[] = [
  { value: "H&E", label: "H&E - Hematoxylin & Eosin" },
  { value: "IHC", label: "IHC - Immunohistochemistry" },
  { value: "ISH", label: "ISH" },
  { value: "Special", label: "Special Stains" },
  { value: "Other", label: "Other" },
];

export interface StainFormValues {
  code: string;
  description: string;
  leicaMarker: string;
  biocareMarker: string;
  stainType: StainType;
  cptId: string;
  isAvailableInHouse: boolean;
  defaultProviderId: string;
  isActive: boolean;
}

interface StainFormProps {
  initialValues?: Stain;
  onSubmit: (values: StainFormValues) => void;
}

export default function StainForm({ initialValues, onSubmit }: StainFormProps) {
  const { data: cptCodes = [] } = useCptCodesAdmin();
  const { data: providers = [] } = useActiveStainProviders();

  const form = useForm<StainFormValues>({
    validateInputOnBlur: true,
    initialValues: {
      code: initialValues?.code || "",
      description: initialValues?.description || "",
      leicaMarker: initialValues?.leicaMarker || "",
      biocareMarker: initialValues?.biocareMarker || "",
      stainType: initialValues?.stainType || "Other",
      cptId: initialValues?.cptId || "",
      isAvailableInHouse: initialValues?.isAvailableInHouse ?? true,
      defaultProviderId: initialValues?.defaultProviderId || "",
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      code: required("Stain code is required"),
      description: required("Description is required"),
      stainType: required("Stain type is required"),
    },
  });

  const isInHouse = form.values.isAvailableInHouse;

  const cptCodeOptions = useMemo(
    () =>
      cptCodes
        .map((c) => ({ value: c.id, label: `${c.code} - ${c.description}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [cptCodes]
  );

  const providerOptions = useMemo(
    () =>
      providers
        .map((p) => ({ value: p.id, label: p.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [providers]
  );

  const handleValidationError = useCallback(() => {
    scrollToFirstError(form);
  }, [form]);

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      form.onSubmit(onSubmit, handleValidationError)(event);
    },
    [form, onSubmit, handleValidationError]
  );

  return (
    <form id="stain-form" noValidate onSubmit={handleFormSubmit}>
      <Stack gap="md">
        <TextInput
          label="Stain Code"
          placeholder="e.g., H&E, CDX2"
          withAsterisk
          {...form.getInputProps("code")}
        />

        <TextInput
          label="Leica/BOND marker"
          placeholder="Reagent Public Name"
          description="Defaults to the stain code if blank"
          {...form.getInputProps("leicaMarker")}
        />

        <TextInput
          label="Biocare/NeoPATH marker"
          placeholder="Technique name"
          description="Defaults to the stain code if blank"
          {...form.getInputProps("biocareMarker")}
        />

        <Textarea
          label="Description"
          placeholder="Enter stain description"
          minRows={2}
          withAsterisk
          {...form.getInputProps("description")}
        />

        <Select
          label="Stain Type"
          placeholder="Select stain type"
          data={STAIN_TYPE_OPTIONS}
          withAsterisk
          {...form.getInputProps("stainType")}
        />

        <Select
          label="CPT Code"
          placeholder="Select associated CPT code"
          data={cptCodeOptions}
          searchable
          clearable
          {...form.getInputProps("cptId")}
        />

        <Switch
          label="Available In-House"
          description="Can GCP perform this stain internally?"
          {...form.getInputProps("isAvailableInHouse", { type: "checkbox" })}
        />

        {!isInHouse && (
          <Select
            label="Default External Provider"
            placeholder="Select default provider"
            description="Which provider should be used by default for this stain?"
            data={providerOptions}
            searchable
            clearable
            {...form.getInputProps("defaultProviderId")}
          />
        )}

        <Switch
          label="Active"
          description="Is this stain available for selection?"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
